// src/pages/ApplicationStoreProducts/components/ProductStoreFilterBox/index.tsx
import { Box, Button, Flex, Heading, Input, InputGroup, SimpleGrid, Text } from '@chakra-ui/react';
import { UseFormReturn, useController } from 'react-hook-form';
import { useDebouncedFieldRegistry } from '@/utils/filterUtils/useDebouncedFieldRegistry';
import { ChakraFeatherIcon } from 'src/components/ChakraFeatherIcon';
import { SelectField } from 'src/components/SelectField';
import { formatDateToDateInputValue } from '@/utils/formatDateToDateInputValue';
import { InputField } from '@/components/InputField';
import { Club, ProductStoreStatus, translateProductStoreStatus } from '@/model';
import { useTranslation } from 'react-i18next';
import { StoreProductsFilters } from '../../types';
import TeamFilterSelect from '@/components/TeamFilterSelect';

export interface StoreFilterBoxProps {
  clubs: Club[];
  filtersForm: UseFormReturn<StoreProductsFilters>;
}

export const StoreFilterBox = ({ filtersForm, clubs }: StoreFilterBoxProps) => {
  const { t } = useTranslation();
  const { register, reset, control } = filtersForm;

  const startDateControl = useController({
    name: 'start_date',
    control,
  });
  const endDateControl = useController({
    name: 'end_date',
    control,
  });

  return (
    <Box>
      <Flex align='center' gap='pico' mb='deka'>
        <ChakraFeatherIcon color='text.main' name='filter' />
        <Heading variant='sectionTitle'>
          {t('pages.application_store.components.product_store_filter.store_filter_title')}
        </Heading>
      </Flex>

      <Flex direction={{ base: 'row' }} gap='small' rowGap='micro'>
        <SimpleGrid
          gap='deka'
          alignItems='flex-end'
          columns={{ base: 1, sm: 2, md: 4, lg: 5, xl: 6 }}
          rowGap='xxsmall'
          flexGrow={1}
        >
          <TeamFilterSelect showFlag name='team_uuid' control={filtersForm.control} clubs={clubs} width='100%' />

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_store.components.product_store_filter.store_filter_product_label')}
            </Text>
            <Input variant='flushed' {...useDebouncedFieldRegistry(() => register('title'))} />
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_store.components.product_store_filter.store_filter_status_label')}
            </Text>
            <SelectField {...register('status')}>
              <option value=''>{t('pages.application_store.components.product_store_filter.store_filter_status_all')}</option>
              <option value={ProductStoreStatus.PUBLISHED}>{translateProductStoreStatus(ProductStoreStatus.PUBLISHED)}</option>
              <option value={ProductStoreStatus.DRAFT}>{translateProductStoreStatus(ProductStoreStatus.DRAFT)}</option>
            </SelectField>
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_store.components.product_store_filter.store_filter_start_date_label')}
            </Text>
            <InputField
              type='date'
              fullDatePickerArea
              variant='flushed'
              value={formatDateToDateInputValue(startDateControl.field.value)}
              onChange={event => startDateControl.field.onChange(event.target.value)}
            />
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_store.components.product_store_filter.store_filter_end_date_label')}
            </Text>
            <InputField
              type='date'
              fullDatePickerArea
              variant='flushed'
              value={formatDateToDateInputValue(endDateControl.field.value)}
              onChange={event => endDateControl.field.onChange(event.target.value)}
              min={formatDateToDateInputValue(startDateControl.field.value)}
            />
          </InputGroup>
        </SimpleGrid>

        <Button alignSelf={{ lg: 'end' }} colorScheme='text' variant='outline' mt='10px' onClick={() => reset()}>
          {t('pages.application_store.components.product_store_filter.store_filter_clear_filters')}
        </Button>
      </Flex>
    </Box>
  );
};
